import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Switch,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import EditVendorDetailsModal from '../Vendors/EditVendorDetailsModal'
import SubcontractorManagementPanel from '../Vendors/SubcontractorManagementPanel'
import NewSupplierModal from '../Proposals/NewSupplierModal'

const useStyles = makeStyles((theme) => ({
  page: {
    padding: theme.spacing(2),
    height: 'calc(100vh - 90px)',
    overflow: 'hidden'
  },
  panel: {
    padding: theme.spacing(2),
    borderRadius: 18,
    height: '100%'
  },
  vendorList: {
    maxHeight: 'calc(100vh - 250px)',
    overflowY: 'auto',
    border: '1px solid #e3e8e4',
    borderRadius: 14
  },
  vendorItem: {
    borderBottom: '1px solid #edf1ee'
  },
  selectedVendor: {
    backgroundColor: '#edf7f1'
  },
  muted: {
    color: '#5d6c64'
  },
  headerCard: {
    background: 'linear-gradient(135deg, rgba(235,245,238,.95) 0%, rgba(245,251,247,.98) 100%)',
    border: '1px solid #d5e2d9'
  },
  summaryRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  metricGrid: {
    marginTop: theme.spacing(2)
  },
  metricCard: {
    borderRadius: 16,
    border: '1px solid #d8e3dc',
    background: '#fff',
    padding: theme.spacing(2),
    height: '100%'
  },
  metricValue: {
    fontSize: '1.9rem',
    fontWeight: 700,
    lineHeight: 1.1
  },
  metricLabel: {
    color: '#5d6c64',
    marginTop: theme.spacing(0.75)
  },
  scrollArea: {
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
    paddingRight: theme.spacing(1)
  },
  emptyState: {
    border: '1px dashed #c5d1c8',
    borderRadius: 16,
    padding: theme.spacing(3),
    textAlign: 'center',
    background: '#f8fbf9'
  },
  statusChip: {
    fontWeight: 600
  },
  cardHeading: {
    fontWeight: 700,
    marginBottom: theme.spacing(1)
  },
  checklistWrap: {
    border: '1px solid #e2e8e4',
    borderRadius: 16,
    padding: theme.spacing(2),
    height: '100%'
  },
  actionItem: {
    paddingLeft: 0,
    paddingRight: 0,
    borderBottom: '1px solid #edf1ee'
  },
  checklistItem: {
    paddingLeft: 0,
    paddingRight: 0
  }
}))

const getEmployeeAccessStatus = (employee) => {
  if (employee?.subcontractorAccessStatus) return employee.subcontractorAccessStatus
  return employee?.activeEmployee === false ? 'offboarded' : 'active'
}

const countVendorUsers = (employees, vendorId) => {
  const scoped = employees.filter(item => String(item.associatedSub || '') === String(vendorId))
  return {
    total: scoped.length,
    active: scoped.filter(item => getEmployeeAccessStatus(item) === 'active').length,
    invited: scoped.filter(item => getEmployeeAccessStatus(item) === 'invited').length,
    offboarded: scoped.filter(item => getEmployeeAccessStatus(item) === 'offboarded').length,
    admins: scoped.filter(item => item.subcontractorRole === 'admin' && getEmployeeAccessStatus(item) !== 'offboarded').length,
    portalEnabled: scoped.filter(item => item.canUseSubcontractorPortal).length
  }
}

const countVendorCrews = (crews, vendorId) => (
  crews.filter(item => String(item.subcontractorVendor || '') === String(vendorId)).length
)

const getStageChipStyle = (stageKey) => {
  switch (stageKey) {
    case 'ready':
      return { backgroundColor: '#e5f4ea', color: '#1b5e20' }
    case 'pending':
      return { backgroundColor: '#eef2ff', color: '#3547a5' }
    default:
      return { backgroundColor: '#fff4e5', color: '#9c5f19' }
  }
}

const getChecklistChipStyle = (complete) => (
  complete
    ? { backgroundColor: '#e5f4ea', color: '#1b5e20' }
    : { backgroundColor: '#eceff1', color: '#455a64' }
)

const getVendorLaunchState = (vendor, userCounts, crewCount) => {
  const onboardingStatus = vendor?.onboardingStatus || 'draft'
  const checklist = [
    {
      label: 'Vendor is marked as a subcontractor',
      complete: !!vendor?.isSubcontractor,
      detail: vendor?.isSubcontractor ? 'This vendor can be managed from the subcontractor admin flow.' : 'Open vendor settings and enable subcontractor access.'
    },
    {
      label: 'Mobile access is enabled',
      complete: !!vendor?.mobileAccessEnabled,
      detail: vendor?.mobileAccessEnabled ? 'Users can access the subcontractor mobile experience.' : 'Turn on mobile access before launch.'
    },
    {
      label: 'Onboarding state is active',
      complete: onboardingStatus === 'active',
      detail: `Current onboarding status: ${onboardingStatus}.`
    },
    {
      label: 'An admin user is assigned',
      complete: userCounts.admins > 0,
      detail: userCounts.admins > 0 ? `${userCounts.admins} admin user linked.` : 'Add at least one subcontractor admin user.'
    },
    {
      label: 'At least one crew is linked',
      complete: crewCount > 0,
      detail: crewCount > 0 ? `${crewCount} linked crew${crewCount === 1 ? '' : 's'}.` : 'Create or assign a crew for this vendor.'
    }
  ]

  const actionItems = []

  if (!vendor?.isSubcontractor) {
    actionItems.push({
      title: 'Enable subcontractor mode',
      detail: 'Convert this vendor into a subcontractor org before inviting users or linking crews.'
    })
  }

  if (vendor?.isSubcontractor && !vendor?.mobileAccessEnabled) {
    actionItems.push({
      title: 'Turn on mobile access',
      detail: 'Portal users will not have the intended subcontractor experience until mobile access is enabled.'
    })
  }

  if (vendor?.isSubcontractor && onboardingStatus !== 'active') {
    actionItems.push({
      title: 'Move onboarding to active',
      detail: 'Keep draft while configuring, then mark active when the vendor is ready to launch.'
    })
  }

  if (vendor?.isSubcontractor && userCounts.admins === 0) {
    actionItems.push({
      title: 'Add an admin user',
      detail: 'Every subcontractor org should have an admin who can receive invites and manage access.'
    })
  }

  if (vendor?.isSubcontractor && userCounts.total === 0) {
    actionItems.push({
      title: 'Create the first portal user',
      detail: 'Use the vendor management panel below to add a subcontractor user and launch the existing invite flow.'
    })
  } else if (userCounts.invited > 0) {
    actionItems.push({
      title: 'Follow up on pending invites',
      detail: `${userCounts.invited} invite${userCounts.invited === 1 ? ' is' : 's are'} still waiting for completion.`
    })
  }

  if (vendor?.isSubcontractor && crewCount === 0) {
    actionItems.push({
      title: 'Link a crew',
      detail: 'Assign at least one subcontractor crew so the vendor setup feels complete and operational.'
    })
  }

  let stageKey = 'ready'
  if (!vendor?.isSubcontractor || !vendor?.mobileAccessEnabled || onboardingStatus !== 'active' || userCounts.admins === 0 || crewCount === 0) {
    stageKey = 'setup'
  } else if (userCounts.invited > 0 || userCounts.active === 0) {
    stageKey = 'pending'
  }

  return {
    crewCount,
    stageKey,
    stageLabel: stageKey === 'ready' ? 'Launch Ready' : stageKey === 'pending' ? 'Invites Pending' : 'Needs Setup',
    nextAction: actionItems[0]?.title || 'Review linked users and crews',
    checklist,
    actionItems: actionItems.length ? actionItems : [{
      title: 'Vendor is ready to launch',
      detail: 'Settings, access, and crew alignment are in place. Keep an eye on new invites and crew changes.'
    }]
  }
}

const getVendorSortPriority = (stageKey) => {
  if (stageKey === 'setup') return 0
  if (stageKey === 'pending') return 1
  return 2
}

const SubcontractorAdminPage = ({ url, notification }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [vendors, setVendors] = useState([])
  const [employees, setEmployees] = useState([])
  const [crews, setCrews] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [showAllVendors, setShowAllVendors] = useState(false)
  const [selectedVendorId, setSelectedVendorId] = useState(null)
  const [editingVendor, setEditingVendor] = useState(null)
  const [showCreateVendorModal, setShowCreateVendorModal] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [vendorsResponse, employeesResponse] = await Promise.all([
        axios.get(`${url}/api/company/getVendors`),
        axios.get(`${url}/api/employees/listEmployees?entity=All&includeInactive=true`)
      ])

      setVendors(vendorsResponse.data || [])
      setEmployees(employeesResponse.data?.employees || [])
      setCrews(employeesResponse.data?.crews || [])
    } catch (error) {
      console.error('Failed to load subcontractor admin data', error)
      notification?.({
        type: 'danger',
        title: 'Load Failed',
        message: 'Unable to load subcontractor vendors right now.'
      })
    } finally {
      setLoading(false)
    }
  }, [notification, url])

  useEffect(() => {
    loadData()
  }, [loadData])

  const visibleVendors = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase()
    let next = vendors || []

    if (!showAllVendors) {
      next = next.filter(item => item.isSubcontractor)
    }

    if (normalizedSearch) {
      next = next.filter(item => {
        const haystack = [
          item.name,
          item.phoneNumber,
          item.address,
          item.vendorType,
          item.city,
          item.state
        ].filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(normalizedSearch)
      })
    }

    return next
  }, [searchValue, showAllVendors, vendors])

  const vendorCounts = useMemo(() => {
    const map = new Map()
    vendors.forEach(vendor => {
      map.set(String(vendor._id), countVendorUsers(employees, vendor._id))
    })
    return map
  }, [employees, vendors])

  const vendorInsights = useMemo(() => {
    const map = new Map()
    vendors.forEach(vendor => {
      const userCounts = vendorCounts.get(String(vendor._id)) || countVendorUsers(employees, vendor._id)
      const crewCount = countVendorCrews(crews, vendor._id)
      map.set(String(vendor._id), {
        userCounts,
        ...getVendorLaunchState(vendor, userCounts, crewCount)
      })
    })
    return map
  }, [crews, employees, vendorCounts, vendors])

  const filteredVendors = useMemo(() => {
    return [...visibleVendors].sort((a, b) => {
      const aInsight = vendorInsights.get(String(a._id))
      const bInsight = vendorInsights.get(String(b._id))
      const priorityDifference = getVendorSortPriority(aInsight?.stageKey) - getVendorSortPriority(bInsight?.stageKey)
      if (priorityDifference !== 0) return priorityDifference
      return (a.name || '').localeCompare(b.name || '')
    })
  }, [vendorInsights, visibleVendors])

  useEffect(() => {
    if (!filteredVendors.length) {
      setSelectedVendorId(null)
      return
    }

    const stillVisible = filteredVendors.some(item => String(item._id) === String(selectedVendorId || ''))
    if (!stillVisible) {
      setSelectedVendorId(filteredVendors[0]._id)
    }
  }, [filteredVendors, selectedVendorId])

  const selectedVendor = useMemo(
    () => filteredVendors.find(item => String(item._id) === String(selectedVendorId || '')) || null,
    [filteredVendors, selectedVendorId]
  )

  const selectedInsight = useMemo(() => {
    if (!selectedVendor?._id) return null
    return vendorInsights.get(String(selectedVendor._id)) || null
  }, [selectedVendor, vendorInsights])

  const pageSummary = useMemo(() => {
    return filteredVendors.reduce((summary, vendor) => {
      const insight = vendorInsights.get(String(vendor._id))
      if (!insight) return summary

      summary.visibleVendors += 1
      summary.ready += insight.stageKey === 'ready' ? 1 : 0
      summary.setup += insight.stageKey === 'setup' ? 1 : 0
      summary.pendingInvites += insight.userCounts.invited
      summary.portalUsers += insight.userCounts.portalEnabled
      summary.crews += insight.crewCount
      return summary
    }, {
      visibleVendors: 0,
      ready: 0,
      setup: 0,
      pendingInvites: 0,
      portalUsers: 0,
      crews: 0
    })
  }, [filteredVendors, vendorInsights])

  const handleVendorUpdated = (updatedVendor) => {
    setVendors((current) => current.map(item => (item._id === updatedVendor._id ? updatedVendor : item)))
    setEditingVendor(null)
    setSelectedVendorId(updatedVendor._id)
  }

  const handleVendorCreated = (newVendor) => {
    setVendors((current) => [newVendor, ...current.filter(item => item._id !== newVendor._id)])
    setShowCreateVendorModal(false)
    setShowAllVendors(true)
    setSelectedVendorId(newVendor._id)
    setEditingVendor(newVendor)
  }

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={32} />
      </div>
    )
  }

  return (
    <div className={classes.page}>
      <Grid container spacing={2} style={{ height: '100%' }}>
        <Grid item xs={12}>
          <Paper className={`${classes.panel} ${classes.headerCard}`} elevation={0}>
            <Typography variant="h4" style={{ fontWeight: 700 }}>
              Subcontractor Admin
            </Typography>
            <Typography variant="body1" className={classes.muted}>
              Manage herbicide subcontractor vendors, portal access, crew alignment, and invitations from one place.
            </Typography>

            <Grid container spacing={2} className={classes.metricGrid}>
              <Grid item md={3} sm={6} xs={12}>
                <Paper elevation={0} className={classes.metricCard}>
                  <div className={classes.metricValue}>{pageSummary.visibleVendors}</div>
                  <Typography variant="body2" className={classes.metricLabel}>
                    Vendors in view
                  </Typography>
                </Paper>
              </Grid>
              <Grid item md={3} sm={6} xs={12}>
                <Paper elevation={0} className={classes.metricCard}>
                  <div className={classes.metricValue}>{pageSummary.setup}</div>
                  <Typography variant="body2" className={classes.metricLabel}>
                    Need setup attention
                  </Typography>
                </Paper>
              </Grid>
              <Grid item md={3} sm={6} xs={12}>
                <Paper elevation={0} className={classes.metricCard}>
                  <div className={classes.metricValue}>{pageSummary.ready}</div>
                  <Typography variant="body2" className={classes.metricLabel}>
                    Launch-ready vendors
                  </Typography>
                </Paper>
              </Grid>
              <Grid item md={3} sm={6} xs={12}>
                <Paper elevation={0} className={classes.metricCard}>
                  <div className={classes.metricValue}>{pageSummary.pendingInvites}</div>
                  <Typography variant="body2" className={classes.metricLabel}>
                    Pending invites
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <div className={classes.summaryRow}>
              <Chip label={`Portal Users ${pageSummary.portalUsers}`} style={{ backgroundColor: '#edf4ff', color: '#23417e' }} />
              <Chip label={`Linked Crews ${pageSummary.crews}`} />
              <Chip label={showAllVendors ? 'Showing all vendors' : 'Focused on subcontractors'} className={classes.statusChip} />
            </div>
          </Paper>
        </Grid>

        <Grid item md={4} xs={12} style={{ height: '100%' }}>
          <Paper className={classes.panel} elevation={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                Vendors
              </Typography>
              <Box display="flex" gridGap={8}>
                <Button size="small" variant="contained" color="primary" onClick={() => setShowCreateVendorModal(true)}>
                  New Vendor
                </Button>
                <Button size="small" variant="outlined" color="primary" onClick={loadData}>
                  Refresh
                </Button>
              </Box>
            </Box>

            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Search vendors"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />

            <FormControlLabel
              style={{ marginTop: 8, marginBottom: 8 }}
              control={(
                <Switch
                  color="primary"
                  checked={showAllVendors}
                  onChange={(e) => setShowAllVendors(e.target.checked)}
                />
              )}
              label={showAllVendors ? 'Showing all vendors' : 'Showing subcontractors only'}
            />

            <div className={classes.vendorList}>
              <List disablePadding>
                {filteredVendors.map((vendor) => {
                  const counts = vendorCounts.get(String(vendor._id)) || { total: 0, active: 0, invited: 0, admins: 0 }
                  const insight = vendorInsights.get(String(vendor._id)) || getVendorLaunchState(vendor, counts, 0)
                  return (
                    <ListItem
                      button
                      key={vendor._id}
                      onClick={() => setSelectedVendorId(vendor._id)}
                      className={`${classes.vendorItem} ${String(selectedVendorId || '') === String(vendor._id) ? classes.selectedVendor : ''}`}
                    >
                      <ListItemText
                        primary={(
                          <Box display="flex" justifyContent="space-between" alignItems="center" gridGap={8} flexWrap="wrap">
                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                              {vendor.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={insight.stageLabel}
                              className={classes.statusChip}
                              style={getStageChipStyle(insight.stageKey)}
                            />
                          </Box>
                        )}
                        secondary={(
                          <Box mt={0.5}>
                            <Typography variant="body2" className={classes.muted}>
                              {insight.nextAction}
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gridGap={8} mt={1}>
                              <Chip size="small" label={vendor.isSubcontractor ? 'Subcontractor org' : 'Standard vendor'} />
                              <Chip size="small" label={`Users ${counts.total}`} />
                              <Chip size="small" label={`Admins ${counts.admins}`} />
                              <Chip size="small" label={`Invites ${counts.invited}`} />
                              <Chip size="small" label={`Crews ${insight.crewCount}`} />
                            </Box>
                          </Box>
                        )}
                      />
                    </ListItem>
                  )
                })}
                {!filteredVendors.length ? (
                  <ListItem>
                    <ListItemText
                      primary="No vendors match this filter"
                      secondary="Enable “show all vendors” if you need to convert an existing labor vendor into a subcontractor org."
                    />
                  </ListItem>
                ) : null}
              </List>
            </div>
          </Paper>
        </Grid>

        <Grid item md={8} xs={12} style={{ height: '100%' }}>
          <div className={classes.scrollArea}>
            {!selectedVendor ? (
              <Paper className={classes.emptyState} elevation={0}>
                <Typography variant="h6" gutterBottom>
                  Select a vendor to manage subcontractor access
                </Typography>
                <Typography variant="body2" className={classes.muted}>
                  This page is designed to promote labor vendors into subcontractor orgs, configure their portal settings, and manage invitations from one workflow.
                </Typography>
              </Paper>
            ) : (
              <>
                <Paper className={classes.panel} elevation={1} style={{ marginBottom: 16 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gridGap={16}>
                    <div>
                      <Typography variant="h5" style={{ fontWeight: 700 }}>
                        {selectedVendor.name}
                      </Typography>
                      <Typography variant="body2" className={classes.muted}>
                        {selectedVendor.address || 'No address on file'}
                      </Typography>
                      <Typography variant="body2" className={classes.muted}>
                        {selectedVendor.phoneNumber || 'No phone number on file'}
                      </Typography>
                    </div>
                    <Button variant="contained" color="primary" onClick={() => setEditingVendor(selectedVendor)}>
                      Edit Subcontractor Settings
                    </Button>
                  </Box>

                  <div className={classes.summaryRow}>
                    <Chip label={selectedInsight?.stageLabel || 'Needs Review'} style={getStageChipStyle(selectedInsight?.stageKey)} className={classes.statusChip} />
                    <Chip label={selectedVendor.isSubcontractor ? 'Subcontractor Enabled' : 'Not Yet Enabled'} style={selectedVendor.isSubcontractor ? { backgroundColor: '#e5f4ea', color: '#1b5e20' } : { backgroundColor: '#fff4e5', color: '#9c5f19' }} />
                    <Chip label={selectedVendor.mobileAccessEnabled ? 'Mobile Enabled' : 'Mobile Disabled'} />
                    <Chip label={`Onboarding ${selectedVendor.onboardingStatus || 'draft'}`} />
                    <Chip label={`Landing ${selectedVendor.defaultLanding || 'spray_contractor_home'}`} />
                    <Chip label={`Users ${(vendorCounts.get(String(selectedVendor._id)) || {}).total || 0}`} />
                    <Chip label={`Admins ${(vendorCounts.get(String(selectedVendor._id)) || {}).admins || 0}`} />
                    <Chip label={`Crews ${selectedInsight?.crewCount || 0}`} />
                  </div>

                  <Typography variant="body2" className={classes.muted}>
                    {selectedInsight?.actionItems?.[0]?.detail || 'Use the vendor settings modal to mark this org as a subcontractor, enable mobile access, and control onboarding state. User invites and offboarding live below.'}
                  </Typography>
                </Paper>

                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                  <Grid item md={6} xs={12}>
                    <Paper className={classes.checklistWrap} elevation={0}>
                      <Typography variant="subtitle1" className={classes.cardHeading}>
                        Launch Checklist
                      </Typography>
                      <List dense disablePadding>
                        {(selectedInsight?.checklist || []).map((item) => (
                          <ListItem key={item.label} className={classes.checklistItem}>
                            <ListItemText primary={item.label} secondary={item.detail} />
                            <Chip
                              size="small"
                              label={item.complete ? 'Ready' : 'Needed'}
                              className={classes.statusChip}
                              style={getChecklistChipStyle(item.complete)}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Paper className={classes.checklistWrap} elevation={0}>
                      <Typography variant="subtitle1" className={classes.cardHeading}>
                        Recommended Next Actions
                      </Typography>
                      <List dense disablePadding>
                        {(selectedInsight?.actionItems || []).map((item) => (
                          <ListItem key={item.title} className={classes.actionItem}>
                            <ListItemText primary={item.title} secondary={item.detail} />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                </Grid>

                <Paper className={classes.panel} elevation={1}>
                  <SubcontractorManagementPanel vendor={selectedVendor} onEditVendor={() => setEditingVendor(selectedVendor)} />
                </Paper>
              </>
            )}
          </div>
        </Grid>
      </Grid>

      <EditVendorDetailsModal
        open={!!editingVendor}
        vendor={editingVendor || {}}
        vendors={vendors}
        closeModal={() => setEditingVendor(null)}
        updateParent={handleVendorUpdated}
      />
      <NewSupplierModal
        open={showCreateVendorModal}
        name=""
        closeModal={() => setShowCreateVendorModal(false)}
        updateParent={handleVendorCreated}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorAdminPage)
